/**
 * The entrance's opening image: a professional steam iron resting on a pressing
 * table, lit from below by its own soleplate.
 *
 * Pure inline SVG — no raster asset, nothing fetched. The realism budget is
 * spent entirely on LIGHT rather than on detail: a dark body picked out by one
 * champagne rim highlight, a hot gold soleplate line, and a warm pool on the
 * table. That is how the concept frame reads too — you see maybe five per cent
 * of an actual iron and the lighting does the rest.
 *
 * Deliberately NOT drawn: brand dials, cords, water windows, printed labels.
 * Every added literal detail moves this from "cinematic object" toward
 * "clip-art of an iron", which the brief forbids.
 *
 * The toe points toward the inline start (left in LTR). The parent mirrors the
 * whole scene for RTL, so the iron faces the reading direction either way.
 */
export function IronScene({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 560 300"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {/* Body metal: near-black with a warm graphite roll-off. The warm cast
            matters — a neutral grey iron would sit outside the stage's palette. */}
        <linearGradient id="lp-iron-body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3b332a" />
          <stop offset="34%" stopColor="#221d18" />
          <stop offset="78%" stopColor="#15110d" />
          <stop offset="100%" stopColor="#0e0b09" />
        </linearGradient>

        {/* The rim light along the body's crown — champagne, not white. */}
        <linearGradient id="lp-iron-rim" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#c6a461" stopOpacity="0" />
          <stop offset="30%" stopColor="#e6d2a2" stopOpacity="0.85" />
          <stop offset="62%" stopColor="#c6a461" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#c6a461" stopOpacity="0" />
        </linearGradient>

        {/* Soleplate heat: brightest at the toe, cooling toward the heel. */}
        <linearGradient id="lp-iron-sole" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#f1e0b6" />
          <stop offset="30%" stopColor="#d8bc7e" />
          <stop offset="75%" stopColor="#a87b34" />
          <stop offset="100%" stopColor="#6b4c1e" />
        </linearGradient>

        {/* The warm pool the soleplate throws onto the table. */}
        <radialGradient id="lp-iron-pool" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#d8bc7e" stopOpacity="0.5" />
          <stop offset="45%" stopColor="#a87b34" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#a87b34" stopOpacity="0" />
        </radialGradient>

        {/* Reflection of the glow on the table surface, directly under the sole. */}
        <linearGradient id="lp-iron-reflect" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e6d2a2" stopOpacity="0.34" />
          <stop offset="100%" stopColor="#e6d2a2" stopOpacity="0" />
        </linearGradient>

        {/* Handle metal — slightly lifted from the body so the loop reads. */}
        <linearGradient id="lp-iron-handle" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4a4036" />
          <stop offset="55%" stopColor="#29221b" />
          <stop offset="100%" stopColor="#16110e" />
        </linearGradient>

        <filter id="lp-iron-soft" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
        <filter id="lp-iron-softer" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="14" />
        </filter>
      </defs>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      {/* The pressing table is implied, not drawn: a hairline of caught light
          along its edge and the warm pool. */}
      <ellipse cx="268" cy="242" rx="248" ry="34" fill="url(#lp-iron-pool)" filter="url(#lp-iron-softer)" />
      <rect x="20" y="238" width="520" height="1.5" fill="url(#lp-iron-rim)" opacity="0.32" />

      {/* ── Reflection under the soleplate ─────────────────────────────────── */}
      <rect x="96" y="240" width="330" height="26" fill="url(#lp-iron-reflect)" filter="url(#lp-iron-soft)" />

      {/* ── Soleplate ──────────────────────────────────────────────────────── */}
      {/* The hot edge — the brightest thing in the frame until the mark appears. */}
      <path
        d="M 78,238 Q 84,224 128,220 L 404,214 Q 434,214 436,230 L 438,238 Z"
        fill="url(#lp-iron-sole)"
      />
      {/* Heat bloom over the plate edge. */}
      <path
        d="M 78,238 Q 84,224 128,220 L 404,214 Q 434,214 436,230 L 438,238 Z"
        fill="url(#lp-iron-sole)"
        filter="url(#lp-iron-soft)"
        opacity="0.6"
      />

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <path
        d="M 80,236
           C 118,178 202,142 286,136
           C 356,131 416,146 434,182
           C 444,202 442,224 436,234
           L 80,236 Z"
        fill="url(#lp-iron-body)"
      />
      {/* Crown rim light: the single stroke that makes the silhouette legible. */}
      <path
        d="M 92,226 C 128,176 206,144 288,139 C 352,135 408,148 428,178"
        fill="none"
        stroke="url(#lp-iron-rim)"
        strokeWidth="2.4"
        strokeLinecap="round"
        opacity="0.9"
      />
      {/* A second, fainter contour lower on the shoulder — gives the dome volume. */}
      <path
        d="M 120,220 C 160,190 230,168 300,163"
        fill="none"
        stroke="#c6a461"
        strokeWidth="1"
        strokeLinecap="round"
        opacity="0.18"
      />

      {/* ── Steam vents on the toe ─────────────────────────────────────────── */}
      {[0, 1, 2, 3].map((i) => (
        <circle
          key={i}
          cx={132 + i * 22}
          cy={212 - i * 2.4}
          r="2"
          fill="#e6d2a2"
          opacity={0.5 - i * 0.08}
        />
      ))}

      {/* ── Handle ─────────────────────────────────────────────────────────── */}
      {/* One thick round-capped stroke reads as a loop without an inner path. */}
      <path
        d="M 244,140 C 268,84 366,82 396,144"
        fill="none"
        stroke="url(#lp-iron-handle)"
        strokeWidth="30"
        strokeLinecap="round"
      />
      {/* Rim light along the top of the grip. */}
      <path
        d="M 252,128 C 276,82 360,80 388,130"
        fill="none"
        stroke="url(#lp-iron-rim)"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.55"
      />

      {/* Thumb rest / dial hinted as one dark ellipse with a warm edge. */}
      <ellipse cx="330" cy="152" rx="26" ry="9" fill="#0e0b09" />
      <ellipse cx="330" cy="151" rx="26" ry="9" fill="none" stroke="#c6a461" strokeWidth="0.8" opacity="0.28" />
    </svg>
  );
}
